import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { getProcedureBundle, pickEn } from './procedureContent';

const ProcedureContext = createContext(null);

const STORAGE_KEY = 'mvp_procedure_slug';

export function ProcedureProvider({ slug, basePath, children }) {
  const [bundle, setBundle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getProcedureBundle(slug)
      .then((data) => {
        if (cancelled) return;
        if (!data) {
          setError('Procedure not found in content feed');
          setBundle(null);
          return;
        }
        setBundle(data);
        try {
          sessionStorage.setItem(STORAGE_KEY, slug);
        } catch {
          /* ignore */
        }
      })
      .catch((err) => {
        if (!cancelled) setError(err?.message || 'Failed to load procedure');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  const value = useMemo(
    () => ({
      slug,
      basePath,
      bundle,
      loading,
      error,
      pickEn,
      procedure: bundle?.card?.procedure || null,
    }),
    [slug, basePath, bundle, loading, error],
  );

  return <ProcedureContext.Provider value={value}>{children}</ProcedureContext.Provider>;
}

export function useProcedure() {
  const ctx = useContext(ProcedureContext);
  if (!ctx) {
    throw new Error('useProcedure must be used within ProcedureProvider');
  }
  return ctx;
}

export function useProcedureOptional() {
  return useContext(ProcedureContext);
}

export function getStoredProcedureSlug() {
  try {
    return sessionStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}
